import { useEffect, useState } from "react";

/**
 * Returns a value that lags behind the input by `delayMs` — used to
 * stop search inputs (and similar text-typing scenarios) from firing
 * one query per keystroke. Bind the input to its own immediate state
 * variable, pass the debounced version into TanStack Query / fetch
 * dependencies.
 *
 * Pattern:
 *   const [search, setSearch] = useState("");
 *   const debouncedSearch = useDebouncedValue(search, 350);
 *   useCampaignsTable(filters, page, status, debouncedSearch);
 *   ...
 *   <input value={search} onChange={(e) => setSearch(e.target.value)} />
 *
 * The input still feels instant (immediate-state drives the visible
 * value), but the API only sees the value after the user has paused
 * typing for `delayMs`.
 *
 * 350 ms is a UX sweet spot — short enough that users don't feel a
 * lag between typing and the results updating, long enough that
 * normal-speed typing produces one final query per word, not one per
 * letter.
 */
export function useDebouncedValue<T>(value: T, delayMs: number = 350): T {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);

  return debounced;
}
